import React from "react";

/**
 * ABNORMAL — Brain glyph primitives.
 *
 * The Brain is not a mascot. It is a symbol of independent thinking.
 * Use these primitives to sprinkle the motif across the site without
 * turning it into decoration.
 *
 *  <BrainGlyph />         → inline stylized brain (rounded lobes + fissure)
 *  <BrainDivider />       → a horizontal repeating divider of small brains
 *  <BrainWatermark />     → massive faint watermark, positioned absolute
 */

const BRAIN_PATH = "M12 3c-1.3 0-2.4.6-3.1 1.5-.6-.3-1.3-.5-2-.5-2.2 0-4 1.8-4 4 0 .9.3 1.7.8 2.4C2.7 11.1 2 12 2 13.2c0 1.5 1 2.7 2.4 3 .1 1.6 1.4 2.9 3.1 2.9.5 0 1-.1 1.4-.3.7 1 1.9 1.7 3.1 1.7 1.3 0 2.4-.6 3.1-1.5.4.1.7.2 1.1.2 2 0 3.6-1.6 3.6-3.6 0-.2 0-.4-.1-.6C21.1 14.4 22 13.3 22 12c0-1.1-.6-2.1-1.5-2.7.3-.6.5-1.3.5-2C21 5.1 19.1 3 16.5 3c-.9 0-1.8.3-2.5.8C13.4 3.3 12.7 3 12 3zm0 3.5v11";

/**
 * Base inline glyph — stylized (not medical). Two lobes + fissure line.
 */
export function BrainGlyph({ size = 24, className = "", strokeWidth = 1.5, ...rest }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            className={className}
            aria-hidden="true"
            {...rest}
        >
            <path d={BRAIN_PATH} />
        </svg>
    );
}

/**
 * Horizontal divider — a row of small brains with hairline rules on both sides.
 * Used to break editorial sections without the corporate `<hr>` feel.
 */
export function BrainDivider({ label, className = "" }) {
    return (
        <div className={`flex items-center gap-4 select-none ${className}`} aria-hidden="true">
            <span className="flex-1 h-px bg-white/10" />
            <div className="flex items-center gap-2 text-white/30">
                <BrainGlyph size={14} strokeWidth={1.4} />
                {label && (
                    <span className="font-vt text-[11px] tracking-[0.4em] uppercase text-white/40">
                        {label}
                    </span>
                )}
                <BrainGlyph size={14} strokeWidth={1.4} />
            </div>
            <span className="flex-1 h-px bg-white/10" />
        </div>
    );
}

/**
 * Watermark — enormous, faint, rotated, non-interactive.
 * Position it with utility classes: absolute inset-0, top-0 right-0, etc.
 */
export function BrainWatermark({ className = "", size = 640, opacity = 0.05, rotate = -12 }) {
    return (
        <div
            className={`pointer-events-none select-none ${className}`}
            style={{ transform: `rotate(${rotate}deg)`, opacity }}
            aria-hidden="true"
        >
            <BrainGlyph size={size} strokeWidth={0.6} className="text-white" />
        </div>
    );
}

/**
 * Half-erased brain — masked from bottom, used behind manifesto copy.
 * Give it a fixed width via className; it will mask automatically.
 */
export function BrainHalfErased({ className = "", size = 520 }) {
    return (
        <div
            className={`pointer-events-none select-none ${className}`}
            style={{
                WebkitMaskImage: "linear-gradient(to bottom, black 30%, transparent 90%)",
                maskImage: "linear-gradient(to bottom, black 30%, transparent 90%)",
            }}
            aria-hidden="true"
        >
            <BrainGlyph size={size} strokeWidth={0.75} className="text-abnormal-red/40" />
        </div>
    );
}

/**
 * BrainSticker — Abnormal team patch (v2).
 *
 * Not a brain "icon". A round patch that looks stitched, screen-printed and
 * slightly misaligned — like a downhill jersey badge or a fanzine stamp.
 *
 *   size    → diameter in px
 *   rotate  → rotation in degrees (default -6°, keeps it hand-placed)
 *   variant → "acid" (default) | "red"
 */
export function BrainSticker({
    size = 96,
    className = "",
    rotate = -6,
    variant = "acid",
}) {
    const isAcid = variant === "acid";
    const fill  = isAcid ? "#C9FF3D" : "#FF3B30";
    const ink   = "#0B0B0B";

    return (
        <div
            className={`relative inline-block pointer-events-none select-none ${className}`}
            style={{ width: size, height: size, transform: `rotate(${rotate}deg)` }}
            aria-hidden="true"
        >
            {/* Misregistration ghost — offset ink layer under the patch */}
            <svg viewBox="0 0 120 120"
                className="absolute inset-0 w-full h-full"
                style={{ transform: "translate(2.5px, 3px)", opacity: 0.55 }}
            >
                <circle cx="60" cy="60" r="52" fill="#FF3B30" />
            </svg>

            {/* Main patch */}
            <svg viewBox="0 0 120 120"
                className="absolute inset-0 w-full h-full"
            >
                {/* die-cut face */}
                <circle cx="60" cy="60" r="52" fill={fill} />
                {/* inner ring */}
                <circle cx="60" cy="60" r="46" fill="none" stroke={ink} strokeWidth="1.2" />

                {/* orbital text along the top arc — like a stamp */}
                <defs>
                    <path id="patch-arc-top"
                        d="M 20 60 A 40 40 0 0 1 100 60" />
                    <path id="patch-arc-bot"
                        d="M 100 60 A 40 40 0 0 1 20 60" />
                </defs>
                <text fill={ink} fontFamily="VT323, monospace" fontSize="9" letterSpacing="2.8">
                    <textPath href="#patch-arc-top" startOffset="50%" textAnchor="middle">
                        ABNORMAL · MONTE ARGENTARIO
                    </textPath>
                </text>
                <text fill={ink} fontFamily="VT323, monospace" fontSize="8" letterSpacing="3">
                    <textPath href="#patch-arc-bot" startOffset="50%" textAnchor="middle">
                        · EST. MMXXVI · N° 26 ·
                    </textPath>
                </text>

                {/* central brain silhouette — chunky, screen-printed */}
                <g transform="translate(60 62)" fill={ink}>
                    <path d="
                        M -18 -2
                        C -20 -12, -10 -18, -2 -14
                        C 2 -20, 12 -20, 16 -12
                        C 24 -12, 26 -2, 20 4
                        C 24 12, 14 18, 6 14
                        C 2 20, -8 20, -12 14
                        C -20 14, -22 4, -18 -2 Z" />
                    {/* fissure highlight */}
                    <path d="M 2 -14 L 2 14" fill="none" stroke={fill} strokeWidth="1.6" strokeLinecap="round" />
                </g>
            </svg>
        </div>
    );
}


export default BrainGlyph;
